import React from 'react';
import {Button, Table, Tag, Typography} from 'antd';
import type {TableColumnsType} from 'antd';
import {useTranslation} from 'react-i18next';
import {PartnerPenaltyItem, PartnerPenaltyStatus, PenaltyCenterMode} from '../graphql/types';
import {canAppeal, displayOrDash, formatIsoDateTime} from '../utils/penaltyCenterUtils';

interface PenaltyCenterTableProps {
    data: PartnerPenaltyItem[];
    loading: boolean;
    /** warehouse 模式多一列 DSP（一个仓下有多个 DSP，不标出来分不清） */
    mode?: PenaltyCenterMode;
    /** 仅「待提交」Tab 为 true；false 时整列不渲染（审核中/通过/驳回无可执行操作） */
    showAction: boolean;
    onAppeal: (record: PartnerPenaltyItem) => void;
    onAbandon: (record: PartnerPenaltyItem) => void;
    /** 点罚单编号进详情页；路由由外层拼（本页在 partner-control 与 delivery 下各挂载一次） */
    onViewDetail: (record: PartnerPenaltyItem) => void;
}

const STATUS_COLOR: Record<string, string> = {
    [PartnerPenaltyStatus.PENDING_SUBMIT]: 'orange',
    [PartnerPenaltyStatus.REVIEWING]: 'blue',
    [PartnerPenaltyStatus.APPROVED]: 'green',
    [PartnerPenaltyStatus.REJECTED]: 'red',
    [PartnerPenaltyStatus.TIMEOUT]: 'default',
};

const PenaltyCenterTable: React.FC<PenaltyCenterTableProps> = ({
    data,
    loading,
    mode = 'dsp',
    showAction,
    onAppeal,
    onAbandon,
    onViewDetail,
}) => {
    const {t} = useTranslation();
    // 申诉截止判断用整表统一的「本次渲染时刻」，避免逐行取时间导致同一屏内基准不一致
    const nowMillis = Date.now();

    const columns: TableColumnsType<PartnerPenaltyItem> = [
        {
            title: t('partnerControl.penaltyCenter.table.penaltyNo'),
            dataIndex: 'penaltyNo',
            key: 'penaltyNo',
            ellipsis: true,
            render: (_: unknown, record) => (
                <Button type="link" size="small" className="px-0" onClick={() => onViewDetail(record)}>
                    {displayOrDash(record.penaltyNo)}
                </Button>
            ),
        },
        ...((mode === 'warehouse'
            ? [
                  {
                      title: t('partnerControl.penaltyCenter.table.dspName'),
                      dataIndex: 'dspName',
                      key: 'dspName',
                      ellipsis: true,
                      render: (v?: string | null) => displayOrDash(v),
                  },
              ]
            : []) as TableColumnsType<PartnerPenaltyItem>),
        {
            title: t('partnerControl.penaltyCenter.table.createdAt'),
            dataIndex: 'creationTime',
            key: 'creationTime',
            ellipsis: true,
            render: formatIsoDateTime,
        },
        {
            title: t('partnerControl.penaltyCenter.table.source'),
            dataIndex: 'penaltySourceDesc',
            key: 'penaltySourceDesc',
            ellipsis: true,
            render: (v?: string | null) => displayOrDash(v),
        },
        {
            title: t('partnerControl.penaltyCenter.table.trackingNumber'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            ellipsis: true,
            render: (v?: string | null) => displayOrDash(v),
        },
        {
            title: t('partnerControl.penaltyCenter.table.amount'),
            dataIndex: 'amount',
            key: 'amount',
            ellipsis: true,
            // 金额由后端按 BigDecimal 序列化成字符串下传，展示时格式化为两位小数
            render: (v?: string | null) => {
                const num = v != null ? parseFloat(v) : NaN;
                return isNaN(num) ? '--' : num.toFixed(2);
            },
        },
        {
            title: t('partnerControl.penaltyCenter.table.driverName'),
            dataIndex: 'driverName',
            key: 'driverName',
            ellipsis: true,
            render: (v?: string | null) => displayOrDash(v),
        },
        {
            title: t('partnerControl.penaltyCenter.table.appealDeadlineAt'),
            dataIndex: 'appealDeadlineAt',
            key: 'appealDeadlineAt',
            ellipsis: true,
            // 截止申诉时间不区分状态一律标红
            render: (v?: string | null) => <Typography.Text type="danger">{formatIsoDateTime(v)}</Typography.Text>,
        },
        {
            title: t('partnerControl.penaltyCenter.table.status'),
            dataIndex: 'status',
            key: 'status',
            ellipsis: true,
            render: (v: string) => (
                <Tag color={STATUS_COLOR[v] ?? 'default'}>
                    {t(`partnerControl.penaltyCenter.status.${v}`, {defaultValue: v})}
                </Tag>
            ),
        },
    ];

    if (showAction) {
        columns.push({
            title: t('partnerControl.penaltyCenter.table.action'),
            key: 'action',
            width: 140,
            render: (_: unknown, record) => {
                // 前端置灰仅为体验优化，是否真的还能操作以后端判定为准
                const enabled = canAppeal(record.status, record.appealDeadlineAt, nowMillis);
                return (
                    <>
                        <Button
                            type="link"
                            size="small"
                            className="px-0"
                            disabled={!enabled}
                            onClick={() => onAppeal(record)}
                        >
                            {t('partnerControl.penaltyCenter.table.appeal')}
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            className="px-0 ml-2"
                            disabled={!enabled}
                            onClick={() => onAbandon(record)}
                        >
                            {t('partnerControl.penaltyCenter.table.abandon')}
                        </Button>
                    </>
                );
            },
        });
    }

    // 操作记录列已移除

    return (
        <Table
            rowKey="id"
            columns={columns}
            dataSource={data}
            loading={loading}
            pagination={false}
            scroll={{x: 'max-content'}}
            size="middle"
            className="mb-4"
            tableLayout="auto"
        />
    );
};

export default PenaltyCenterTable;
